import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IconButton,
  InputAdornment,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Link,
  Container,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { Formik, Field } from "formik";
import { useTranslation } from "react-i18next";
import Iconify from "../../../components/Iconify";
import {
  useRegisterMutation,
  useEditClientMutation,
  useResendClientOTPMutation,
  useVerifyClientMutation,
} from "../../../pages/login/loginSlice";
import Schema from "./Schema";
import { Message } from "../../../models";
import { RootState } from "../../../store";
import {
  getCurrencyOptions,
  workingDaysPerWeekOptions,
  workingHoursPerDayOptions,
} from "../../../constants";
import PhoneNumberField from "../../../components/PhoneNumberField";
import { shadow } from "../../../utils/cssStyles";
import { Address } from "../../../components/Address";
import OTPInput from "../../../components/OTPInput";
import Alert from "../../../components/Alert";
import { toErrorMsg } from "../../../utils";
import TextField from "../../../components/TextField";
import SingleSelect from "../../../components/SingleSelect";
import FormikAutoScroll from "../../../components/FormikAutoScroll";

export interface IForm {
  setIsVerified: (isVerified: boolean) => void | null;
}
export default ({ setIsVerified }: IForm) => {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");
  const navigate = useNavigate();
  const theme = useTheme();
  const appUser = useSelector((state: RootState) => state.appUser);
  const { t } = useTranslation();
  const [registerPost] = useRegisterMutation();
  const [editClient] = useEditClientMutation();
  const [resendOTP, { isLoading: isResendingOTP }] =
    useResendClientOTPMutation();
  const [verifyClient, { isLoading: isVerifying }] = useVerifyClientMutation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isClientCreated, setIsClientCreated] = useState(false);
  const [otp, setOtp] = useState("");
  const [data, setData] = useState<{
    clientId: string;
    branchId: string;
    adminUserId: string;
    otpSentTo: string;
    otpSentAt: Date | null;
  }>({
    clientId: "",
    branchId: "",
    adminUserId: "",
    otpSentTo: "",
    otpSentAt: null,
  });

  const initialValues: any = {
    name: "",
    tagLine: "",
    phone: "",
    alternatePhone: "",
    email: "",
    description: "",
    logo: "",

    userName: "",
    userLoginUserName: "",
    userPassword: "",
    userConfirmPassword: "",
    userPhone: "",
    userEmail: "",
    userAddress: "",
    userPaymentRate: "",
    userPaymentTerm: "hour",
    userWorkingHoursPerDay: "",
    userWorkingDaysPerWeek: "",

    branchName: "",
    branchPhone: "",
    branchAlternatePhone: "",
    branchEmail: "",
    branchAddress: "",
    currency: "",
    workingHoursPerDay: "",
    workingDaysPerWeek: "",

    refCode: refCode || "",

    // branchUserName: "",
    // branchUserLoginUserName: "",
    // branchUserPassword: "",
    // branchUserConfirmPassword: "",
    // branchUserPhone: "",
    // branchUserEmail: "",
    // branchUserAddress: "",
    // branchUserPaymentRate: "",
    // branchUserPaymentTerm: "hour",
  };

  const [message, setMessage] = useState<Message | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const currencyOptions = useMemo(
    () => getCurrencyOptions(t),
    [appUser.language],
  );

  const handleClick = () => {
    navigate("/", { replace: true });
  };

  const handleVerifyOTP = async () => {
    try {
      setMessage(null);
      if (otp?.length === 4) {
        const { isVerified, error, invalidData }: any = await verifyClient({
          id: data.clientId,
          otp,
        }).unwrap();
        if (isVerified) {
          setIsVerified(isVerified);
          setMessage({
            type: "success",
            message: t("OTPVerifiedSuccessfully"),
          });
        }
        if (error) {
          setMessage({ type: "error", message: toErrorMsg(error) });
        }
        if (invalidData) {
          setMessage({ type: "error", message: toErrorMsg(invalidData) });
        }
      } else {
        setMessage({
          type: "error",
          message: t("PleaseEnterValidOTP"),
        });
      }
    } catch (error: any) {
      setMessage({ type: "error", message: error.message });
    }
  };

  const onResendOTP = async () => {
    try {
      setMessage(null);
      const { isSent, otpSentTo, error, invalidData }: any = await resendOTP({
        id: data.clientId,
      }).unwrap();
      if (isSent) {
        setData({ ...data, otpSentTo, otpSentAt: new Date() });
        setIsClientCreated(true);
        setMessage({
          type: "success",
          message: t("EnterOtp", { sentTo: otpSentTo }),
        });
      }
      if (error) {
        setMessage({ type: "error", message: toErrorMsg(error) });
      }
      if (invalidData) {
        setMessage({ type: "error", message: toErrorMsg(invalidData) });
      }
    } catch (error: any) {
      setMessage({ type: "error", message: error.message });
    }
  };

  return (
    <Grid>
      {isClientCreated && (
        // <Grid>
        //   <Typography sx={{ textAlign: "right" }}>
        //     <Link
        //       variant="subtitle2"
        //       sx={{ cursor: "pointer" }}
        //       onClick={() => setIsClientCreated(false)}
        //     >
        //       {t("GoBackToEdit")}
        //     </Link>
        //   </Typography>
        //   <Typography variant="h4" sx={{ color: "#057d12" }}>
        //     {t("Registrationsuccess")}
        //   </Typography>
        //   <Typography variant="h4" sx={{ color: "#057d12" }}>
        //     {t("VerificationLinkSent")}
        //   </Typography>
        // </Grid>
        <Container maxWidth="sm">
          {!(isResendingOTP || isVerifying) && (
            <Grid sx={{ mb: 3, pr: 1 }}>
              <Typography sx={{ textAlign: "right" }}>
                <Link
                  variant="subtitle2"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setIsClientCreated(false)}
                >
                  {t("GoBackToEdit")}
                </Link>
              </Typography>
            </Grid>
          )}
          <Card style={shadow}>
            <CardHeader
              title={t("EnterOtp", { sentTo: data.otpSentTo })}
              subheader={""}
            />
            <CardContent>
              <FormControl
                size="small"
                fullWidth
                sx={{
                  p: 2,
                  alignItems: "center",
                }}
              >
                <OTPInput
                  otp={otp}
                  onChange={(newValue: string) => {
                    setOtp(newValue);
                  }}
                />
              </FormControl>

              <Grid sx={{ mt: "20px" }}>
                <LoadingButton
                  fullWidth
                  size="small"
                  type="button"
                  variant="contained"
                  loading={isVerifying}
                  loadingPosition="start"
                  disabled={!otp || otp.length < 4}
                  onClick={handleVerifyOTP}
                >
                  {t("Verify")}
                </LoadingButton>

                {!(isResendingOTP || isVerifying) && (
                  <Grid>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, mb: 1, textAlign: "right" }}
                    >
                      <Link
                        variant="subtitle2"
                        sx={{
                          cursor:
                            isResendingOTP || isVerifying
                              ? "default"
                              : "pointer",
                          color:
                            isResendingOTP || isVerifying
                              ? "#878787"
                              : theme.palette.primary.main,
                        }}
                        onClick={() => {
                          if (!(isResendingOTP || isVerifying)) {
                            onResendOTP();
                          }
                        }}
                      >
                        {t("ResendOTP")}
                      </Link>
                    </Typography>
                  </Grid>
                )}

                {message?.type === "error" && (
                  <div
                    style={{
                      marginTop: "15px",
                      color: theme.palette.error.main,
                    }}
                  >
                    {message.message}
                  </div>
                )}

                {message?.type === "success" && (
                  <Alert message={message} onClose={() => setMessage(null)} />
                )}
              </Grid>
            </CardContent>
          </Card>
        </Container>
      )}
      <Grid sx={{ display: isClientCreated ? "none" : "block" }}>
        <Formik
          initialValues={initialValues}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={Schema(t)}
          onSubmit={async (values, actions) => {
            try {
              // if (values.userLoginUserName === values.branchUserLoginUserName) {
              //   setMessage({
              //     type: "error",
              //     message: t("SameOrgandBranchUserName"),
              //   });
              // } else {
              if (values.userPassword !== values.userConfirmPassword) {
                setMessage({
                  type: "error",
                  message: t("AdminUserPasswordNotMatch"),
                });
                // } else if (
                //   values.branchUserPassword !== values.branchUserConfirmPassword
                // ) {
                //   setMessage({
                //     type: "error",
                //     message: t("BranchUserPasswordNotMatch"),
                //   });
              } else {
                if (values.userPaymentRate === "") {
                  values.userPaymentRate = 0;
                }
                if (values.userWorkingHoursPerDay === "") {
                  values.userWorkingHoursPerDay = 0;
                }
                if (values.userWorkingDaysPerWeek === "") {
                  values.userWorkingDaysPerWeek = 0;
                }
                values.phone = values.userPhone;
                values.email = values.userEmail;
                values.branchPhone = values.userPhone;
                values.branchEmail = values.userEmail;

                // if (values.branchUserPaymentRate === "") {
                //   values.branchUserPaymentRate = 0;
                // }
                setMessage(null);

                const payload: any = {
                  ...values,
                  address: values.branchAddress,
                };

                if (data?.clientId?.length > 0) {
                  actions.setSubmitting(true);
                  const { isUpdated, otpSentTo, error, invalidData }: any =
                    await editClient({
                      ...payload,
                      clientId: data.clientId,
                      branchId: data.branchId,
                      adminUserId: data.adminUserId,
                    }).unwrap();

                  actions.setSubmitting(false);
                  if (isUpdated) {
                    setData({ ...data, otpSentTo, otpSentAt: new Date() });
                    setIsClientCreated(true);
                    setMessage({
                      type: "success",
                      message: t("EnterOtp", { sentTo: otpSentTo }),
                    });
                    // const message = {
                    //   type: "success",
                    //   message: t("Registrationsuccess"),
                    // };
                    // window.location.href = "/";
                  }
                  if (error) {
                    setMessage({ type: "error", message: toErrorMsg(error) });
                  }
                  if (invalidData) {
                    actions.setErrors(invalidData);
                    setMessage({
                      type: "error",
                      message: t("Pleasecorrecterrors2"),
                    });
                  }
                } else {
                  actions.setSubmitting(true);
                  const {
                    isCreated,
                    clientId,
                    branchId,
                    adminUserId,
                    otpSentTo,
                    error,
                    invalidData,
                  }: any = await registerPost(payload).unwrap();
                  actions.setSubmitting(false);
                  if (isCreated) {
                    setData({
                      clientId,
                      branchId,
                      adminUserId,
                      otpSentTo,
                      otpSentAt: new Date(),
                    });
                    setIsClientCreated(true);
                    setMessage({
                      type: "success",
                      message: t("EnterOtp", { sentTo: otpSentTo }),
                    });
                    // const message = {
                    //   type: "success",
                    //   message: t("Registrationsuccess"),
                    // };
                    // window.location.href = "/";
                  }
                  if (error) {
                    setMessage({ type: "error", message: toErrorMsg(error) });
                  }
                  if (invalidData) {
                    actions.setErrors(invalidData);
                    setMessage({
                      type: "error",
                      message: t("Pleasecorrecterrors2"),
                    });
                  }
                }
              }
              // }
            } catch (error: any) {
              setMessage({ type: "error", message: error.message });
            }
          }}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <Card style={shadow}>
                <CardHeader
                  title={t("OrganizationInformation")}
                  subheader={""}
                />
                <CardContent sx={{ pt: 1.5 }}>
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="name"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="name"
                              label={t("Name")}
                              placeholder={t("EnterOrgnizationName")}
                              // required={true}
                              autoFocus={true}
                              error={!!(isSubmitted && props.errors?.name)}
                              helperText={isSubmitted && props?.errors?.name}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="tagLine"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="tagLine"
                              label={t("TagLine")}
                              placeholder={t("EnterOrgnizationTagLine")}
                              error={!!(isSubmitted && props.errors?.tagLine)}
                              helperText={isSubmitted && props.errors?.tagLine}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    {/* 
                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="phone"
                      render={({ field }: any) => (
                        <PhoneNumberField
                          field={field}
                          props={props}
                          defaultCountry={"in"}
                          name="phone"
                          label={t("Phone")}
                          placeholder={t("EnterOrgnizationPhoneNumber")}
                          required={true}
                          variant="outlined"
                          error={
                            !!(isSubmitted && props?.errors?.phone)
                          }
                          helperText={
                            isSubmitted && props?.errors?.phone
                          }
                          value={props.values.phone}                          
                          onChange={(val: any) => {
                          props.setFieldValue("phone", val);
                          }}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="email"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="email"
                          label={t("Email")}
                          type="email"
                          placeholder={t("EnterOrgnizationEmail")}
                          error={
                            !!(isSubmitted && props?.errors?.email)
                          }
                          helperText={
                            isSubmitted && props?.errors?.email
                          }                          
                        />
                      )}
                    />
                  </FormControl> */}
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mt: 5, mb: 5 }} style={shadow}>
                <CardHeader title={t("MainBranchInformation")} subheader={""} />
                <CardContent sx={{ pt: 1.5 }}>
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="branchName"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="branchName"
                              label={t("BranchName")}
                              placeholder={t("EnterBranchName")}
                              // required={true}
                              error={
                                !!(isSubmitted && props.errors?.branchName)
                              }
                              helperText={
                                isSubmitted && props.errors?.branchName
                              }
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    {/* 
                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchPhone"
                      render={({ field }: any) => (
                        <PhoneNumberField
                          field={field}
                          props={props}
                          defaultCountry={"in"}
                          name="branchPhone"
                          label={t("BranchPhone")}
                          placeholder={ t("EnterBranchPhoneNumber")}
                          required={true}
                          variant="outlined"
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchPhone)
                          }
                          helperText={
                            isSubmitted && props.errors.branchPhone
                          }
                          value={props.values.branchPhone}                          
                          onChange={(val: any) => {
                          props.setFieldValue("branchPhone", val);
                          }}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchEmail"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchEmail"
                          label={t("BranchEmail")}
                          type="email"
                          placeholder={t("EnterBranchEmail")}
                          error={
                            !!(isSubmitted && props.errors?.branchEmail)
                          }
                          helperText={
                            isSubmitted && props.errors?.branchEmail
                          }                          
                        />
                      )}
                    />
                  </FormControl> */}

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="currency"
                          render={({ field }: any) => (
                            <SingleSelect
                              field={field}
                              name="currency"
                              label={t("SelectCurrency")}
                              // required={true}
                              error={!!(isSubmitted && props.errors?.currency)}
                              helperText={isSubmitted && props.errors?.currency}
                              options={currencyOptions}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="workingHoursPerDay"
                          render={({ field }: any) => (
                            <SingleSelect
                              field={field}
                              name="workingHoursPerDay"
                              label={t("WorkingHoursPerDay")}
                              placeholder={t("EnterWorkingHoursPerDay")}
                              // required={true}
                              error={
                                !!(
                                  isSubmitted &&
                                  props.errors?.workingHoursPerDay
                                )
                              }
                              helperText={
                                isSubmitted && props.errors?.workingHoursPerDay
                              }
                              options={workingHoursPerDayOptions}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="workingDaysPerWeek"
                          render={({ field }: any) => (
                            <SingleSelect
                              field={field}
                              name="workingDaysPerWeek"
                              label={t("WorkingDaysPerWeek")}
                              placeholder={t("EnterWorkingDaysPerWeek")}
                              // required={true}
                              error={
                                !!(
                                  isSubmitted &&
                                  props.errors?.workingDaysPerWeek
                                )
                              }
                              helperText={
                                isSubmitted && props.errors?.workingDaysPerWeek
                              }
                              options={workingDaysPerWeekOptions}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    {/* <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={12}
                      xl={12}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="branchAddress"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="branchAddress"
                              label={t("BranchAddress")}
                              type="text"
                              placeholder={t("EnterBranchAddress")}
                              multiline={true}
                              maxRows={4}
                              error={
                                !!(isSubmitted &&
                                props.errors?.branchAddress)
                              }
                              helperText={
                                isSubmitted &&
                                props.errors?.branchAddress
                              }                              
                            />
                          )}
                        />
                      </FormControl>
                    </Grid> */}

                    <Address
                      t={t}
                      language={appUser.language}
                      data={props.values.branchAddress}
                      error={isSubmitted && props.errors?.branchAddress}
                      onChange={(address: string) => {
                        props.setFieldValue("branchAddress", address);
                      }}
                      // required={true}
                    />

                    {/* <Card sx={{ mt: 2, mb: 2 }}>
                <CardHeader
                  title={t("MainBranchUserInformation")}
                  subheader={t("MainBranchFirstAdminUser")}
                />
                <CardContent sx={{ pt: 1.5 }}>
                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserName"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchUserName"
                          label={t("Name")}
                          placeholder={t("EnterName")}
                          required={true}
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserName)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserName
                          }                          
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserLoginUserName"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchUserLoginUserName"
                          label={t("UserName")}
                          placeholder={t("EnterUserName")}
                          required={true}
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserLoginUserName)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserLoginUserName
                          }                          
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserPassword"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchUserPassword"
                          label={t("Password")}
                          type={showBranchUserPassword ? "text" : "password"}
                          placeholder={t("EnterPassword")}
                          required={true}
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserPassword)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserPassword
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowBranchUserPassword(
                                      !showBranchUserPassword
                                    )
                                  }
                                  edge="end"
                                >
                                  <Iconify
                                    icon={
                                      showBranchUserPassword
                                        ? "eva:eye-fill"
                                        : "eva:eye-off-fill"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                            hasVS={false}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserConfirmPassword"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchUserConfirmPassword"
                          label={t("ConfirmPassword")}
                          type={showBranchUserPassword ? "text" : "password"}
                          placeholder={t("EnterConfirmPassword")}
                          required={true}
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserConfirmPassword)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserConfirmPassword
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowBranchUserPassword(
                                      !showBranchUserPassword
                                    )
                                  }
                                  edge="end"
                                >
                                  <Iconify
                                    icon={
                                      showBranchUserPassword
                                        ? "eva:eye-fill"
                                        : "eva:eye-off-fill"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          hasVS={false}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserPhone"
                      render={({ field }: any) => (                        
                        <PhoneNumberField
                          field={field}
                          defaultCountry={"in"}
                          name="branchUserPhone"
                          label={t("Phone")}
                          placeholder={t("Enteruserphonenumber")}
                          required={true}
                          variant="outlined"
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserPhone)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserPhone
                          }
                          value={props.values.branchUserPhone}                          
                          onChange={(val: any) => {
                          props.setFieldValue("branchUserPhone", val);
                          }}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserEmail"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchUserEmail"
                          label={t("Email")}
                          type="email"
                          placeholder={t("Enteruseremail")}
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserEmail)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserEmail
                          }                          
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="branchUserAddress"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="branchUserAddress"
                          label={t("Address")}
                          placeholder={t("EnteranAddressuser")}
                          multiline={true}
                          maxRows={4}
                          error={
                            !!(isSubmitted &&
                            props.errors?.branchUserAddress)
                          }
                          helperText={
                            isSubmitted &&
                            props.errors?.branchUserAddress
                          }                          
                        />
                      )}
                    />
                  </FormControl>

                  <Grid container>
                    <Grid item sx={{ width: "50%" }}>
                      <FormControl size="small" fullWidth sx={{ p: 2 }}>
                        <Field
                          name="branchUserPaymentRate"
                          render={({ field }: any) => (
                            <TextField
                            field={field}
                            props={props}
                              name="branchUserPaymentRate"
                              label={t("PaymentRate")}
                              type="number"
                              placeholder={t("EnterpaymentRate")}
                              error={
                                !!(isSubmitted &&
                                props.errors?.branchUserPaymentRate)
                              }
                              helperText={
                                isSubmitted &&
                                props.errors?.branchUserPaymentRate
                              }
                              onWheel={(e: any) => e.target.blur()}                              
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item sx={{ width: "50%" }}>
                      <FormControl size="small" fullWidth sx={{ p: 2 }}>
                        <Field
                          name="branchUserPaymentTerm"
                          render={({ field }: any) => (
                            <SingleSelect
                            field={field}
                              name="branchUserPaymentTerm"
                              label={t("SelectPaymentTerm")}
                              error={
                                !!(isSubmitted &&
                                props.errors?.branchUserPaymentTerm)
                              }
                              helperText={
                                isSubmitted &&
                                props.errors?.branchUserPaymentTerm
                              }
                                options={paymentTermOptions}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card> */}
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2, mb: 5 }} style={shadow}>
                <CardHeader
                  title={t("UserInformation")}
                  subheader={t("FirstAdminUser")}
                />
                <CardContent sx={{ pt: 1.5 }}>
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="userName"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="userName"
                              label={t("Name")}
                              placeholder={t("EnterName")}
                              // required={true}
                              error={!!(isSubmitted && props.errors?.userName)}
                              helperText={isSubmitted && props.errors?.userName}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="userLoginUserName"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="userLoginUserName"
                              label={t("UserName")}
                              placeholder={t("EnterUserName")}
                              // required={true}
                              error={
                                !!(
                                  isSubmitted && props.errors?.userLoginUserName
                                )
                              }
                              helperText={
                                isSubmitted && props.errors?.userLoginUserName
                              }
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="userPassword"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="userPassword"
                              label={t("Password")}
                              type={showPassword ? "text" : "password"}
                              placeholder={t("EnterPassword")}
                              // required={true}
                              error={
                                !!(isSubmitted && props.errors?.userPassword)
                              }
                              helperText={
                                isSubmitted && props.errors?.userPassword
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setShowPassword(!showPassword)
                                      }
                                      edge="end"
                                    >
                                      <Iconify
                                        icon={
                                          showPassword
                                            ? "eva:eye-fill"
                                            : "eva:eye-off-fill"
                                        }
                                      />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              hasVS={false}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="userConfirmPassword"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="userConfirmPassword"
                              label={t("ConfirmPassword")}
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder={t("EnterConfirmPassword")}
                              // required={true}
                              error={
                                !!(
                                  isSubmitted &&
                                  props.errors?.userConfirmPassword
                                )
                              }
                              helperText={
                                isSubmitted && props.errors?.userConfirmPassword
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setShowConfirmPassword(
                                          !showConfirmPassword,
                                        )
                                      }
                                      edge="end"
                                    >
                                      <Iconify
                                        icon={
                                          showConfirmPassword
                                            ? "eva:eye-fill"
                                            : "eva:eye-off-fill"
                                        }
                                      />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              hasVS={false}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="userPhone"
                          render={({ field }: any) => (
                            <PhoneNumberField
                              field={field}
                              props={props}
                              defaultCountry={"in"}
                              name="userPhone"
                              label={t("Phone")}
                              placeholder={t("Enteruserphonenumber")}
                              // required={true}
                              variant="outlined"
                              error={!!(isSubmitted && props.errors?.userPhone)}
                              helperText={
                                isSubmitted && props.errors?.userPhone
                              }
                              value={props.values.userPhone}
                              onChange={(val: any) => {
                                props.setFieldValue("userPhone", val);
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      xl={6}
                      sx={{ p: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <Field
                          name="userEmail"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="userEmail"
                              label={t("Email")}
                              type="email"
                              placeholder={t("Enteruseremail")}
                              // required={true}
                              error={!!(isSubmitted && props.errors?.userEmail)}
                              helperText={
                                isSubmitted && props.errors?.userEmail
                              }
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2, mb: 5 }} style={shadow}>
                <CardHeader
                  title={t("Register.ReferralTitle")}
                  subheader={t("Register.ReferralDescription")}
                />
                <CardContent sx={{ pt: 1.5 }}>
                  <Grid container>
                    <Grid item xs={12} sx={{ p: 2 }}>
                      <FormControl size="small" fullWidth>
                        <Field
                          name="refCode"
                          render={({ field }: any) => (
                            <TextField
                              field={field}
                              props={props}
                              name="refCode"
                              label={t("ReferralCode")}
                              placeholder={t("EnterReferralCode")}
                              // required={true}
                              error={!!(isSubmitted && props.errors?.refCode)}
                              helperText={isSubmitted && props.errors?.refCode}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Typography sx={{ mt: 5, mb: 5, pl: 1 }}>
                {!(data?.clientId?.length == 0) && t("AcceptPrivacyPolicy")}
              </Typography>

              <LoadingButton
                fullWidth
                size="small"
                type="submit"
                variant="contained"
                loading={props.isSubmitting}
                // disabled={props.isSubmitting}
                // onClick={handleClick}
                onClick={() => {
                  setIsSubmitted(true);
                  setMessage(null);
                  // if (
                  //   props.values.userLoginUserName ===
                  //   props.values.branchUserLoginUserName
                  // ) {
                  //   setMessage({
                  //     type: "error",
                  //     message: t("SameOrgandBranchUserName"),
                  //   });
                  // } else
                  if (
                    props.values.userPassword !==
                    props.values.userConfirmPassword
                  ) {
                    setMessage({
                      type: "error",
                      message: t("AdminUserPasswordNotMatch"),
                    });
                  }
                  // else if (
                  //   props.values.branchUserPassword !==
                  //   props.values.branchUserConfirmPassword
                  // ) {
                  //   setMessage({
                  //     type: "error",
                  //     message: t("BranchUserPasswordNotMatch"),
                  //   });
                  // }
                }}
              >
                {data?.clientId?.length > 0 ? t("Update") : t("Register")}
              </LoadingButton>
              <FormikAutoScroll props={props} />

              {message?.message && (
                <div
                  style={{ marginTop: "15px", color: theme.palette.error.main }}
                >
                  {message.message}
                </div>
              )}
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};
